<template>
  <div>
    <a-form
      class="mt-3"
      :form="form.fc"
      v-bind="formItemLayout"
      hideRequiredMark>
      <a-form-item :label="$t('bill.budget_alert_type')">
        <a-radio-group v-decorator="decorators.alert_type">
          <a-radio-button v-for="item in ALERT_TYPES" :value="item.key" :key="item.key">{{ item.value }}</a-radio-button>
        </a-radio-group>
      </a-form-item>
      <a-form-item :label="$t('bill.budget_alert_threshold')" :extra="extra">
        <a-row type="flex" justify="start">
          <a-col>
            <a-input-number v-decorator="decorators.alert_threshold" precision="2" min="0" style="width: 300px" />
          </a-col>
          <a-col>
            <a-select v-decorator="decorators.alert_threshold_type">
              <a-select-option v-for="item in ALERT_THRESHOLD_TYPES" :value="item.key" :key="item.key">{{ item.value }}</a-select-option>
            </a-select>
          </a-col>
        </a-row>
      </a-form-item>
      <a-form-item :label="$t('bill.alert_frequency')">
        <a-select v-decorator="decorators.alert_frequency">
          <a-select-option v-for="item in SENDING_FREQUENCYS" :value="item.key" :key="item.key">{{ item.value }}</a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item :label="$t('bill.budget_alert_user')">
        <a-spin v-show="!recipientsLoaded" />
        <base-select
          v-show="recipientsLoaded"
          v-decorator="decorators.recipients"
          optionLabelProp="label"
          class="w-100"
          :filterable="true"
          needParams
          resource="receivers"
          version="v1"
          @change="contactArrOptsChange"
          :params="recipientParams"
          :resList.sync="recipientOpts"
          :initLoaded.sync="recipientsLoaded"
          :select-props="{ placeholder: $t('bill.budget_alert_user_placeholder'), allowClear: true, mode: 'multiple' }" />
      </a-form-item>
      <a-form-item :label="$t('common_599')">
        <a-checkbox-group
          v-decorator="decorators.channel">
          <a-checkbox
            v-for="v in contactArrOpts"
            :key="v.label"
            :value="v.value"
            :disabled="v.disabled">
            <template v-if="v.disabled">
              <a-tooltip placement="top">
                <template slot="title">
                  {{$t('monitor.commonalerts.channel.disable.tips', [v.label])}}
                </template>
                {{ v.label }}
              </a-tooltip>
            </template>
            <template v-else>
              {{ v.label }}
            </template>
          </a-checkbox>
        </a-checkbox-group>
      </a-form-item>
      <notify-types
        :label="$t('monitor.text_11')"
        :placeholder="$t('common.tips.select', [$t('monitor.text_11')])"
        :decorator="decorators.robot_ids" />
    </a-form>
  </div>
</template>

<script>
import _ from 'lodash'
import * as R from 'ramda'
import { mapGetters } from 'vuex'
import { contactMap } from '@/constants'
import NotifyTypes from '@/sections/NotifyTypes'
import { uuid } from '@/utils/utils'
import { ALERT_TYPES, ALERT_THRESHOLD_TYPES, SENDING_FREQUENCYS } from '../../constants'

export default {
  name: 'BudgetAlertForm',
  components: {
    NotifyTypes,
  },
  data () {
    const {
      alert_threshold_type = 'percent',
      alert_threshold = '',
      alert_frequency = '6h',
      user_ids,
      channel = [],
      robot_ids = [],
    } = this.$route.query
    const showChannel = !R.isEmpty(channel)
    const _channel = ['webconsole', ...channel]
    return {
      ALERT_TYPES,
      ALERT_THRESHOLD_TYPES,
      SENDING_FREQUENCYS,
      recipientsLoaded: false, // 报警接收人是否加载完成
      contactArrOpts: [],
      recipientOpts: [],
      showChannel,
      hadRobot: false,
      form: {
        fc: this.$form.createForm(this, {
          onValuesChange: (props, values) => {
            Object.keys(values).forEach((key) => {
              this.form.fd[key] = values[key]
            })
          },
        }),
        fd: {
          alert_type: 'cost',
          alert_threshold_type: alert_threshold_type,
          alert_threshold: alert_threshold,
          alert_frequency: alert_frequency,
        },
      },
      decorators: {
        alert_type: [
          'alert_type',
          {
            initialValue: 'cost',
          },
        ],
        alert_threshold: [
          'alert_threshold',
          {
            initialValue: alert_threshold,
            validateFirst: true,
            validateTrigger: 'blur',
            rules: [
              { required: true, message: this.$t('bill.budget_alert_threshold_required') },
              { validator: this.validateAlertThreshold },
            ],
          },
        ],
        alert_frequency: [
          'alert_frequency',
          {
            initialValue: alert_frequency,
          },
        ],
        alert_threshold_type: [
          'alert_threshold_type',
          {
            initialValue: alert_threshold_type,
          },
        ],
        recipients: [
          'recipients',
          {
            initialValue: user_ids ? (Array.isArray(user_ids) ? user_ids : [user_ids]) : undefined,
            rules: [
              { required: true, message: this.$t('bill.budget_alert_user_placeholder'), type: 'array' },
            ],
          },
        ],
        showChannel: [
          'showChannel',
          {
            valuePropName: 'checked',
            initialValue: showChannel,
          },
        ],
        channel: [
          'channel',
          {
            initialValue: _channel,
          },
        ],
        robot_ids: [
          'robot_ids',
          {
            initialValue: robot_ids ? (Array.isArray(robot_ids) ? robot_ids : [robot_ids]) : [],
          },
        ],
      },
      formItemLayout: {
        wrapperCol: {
          span: 19,
        },
        labelCol: {
          span: 5,
        },
      },
      recipientParams: {
        is_system_account: false,
      },
    }
  },
  computed: {
    ...mapGetters(['isDomainMode', 'scope']),
    extra () {
      if (!this.form.fd.alert_threshold) return ''
      if (this.form.fd.alert_threshold_type === 'amount') {
        return `${this.$t('bill.budget_alert_threshold_type_extra')}${this.form.fd.alert_threshold}`
      }
      return `${this.$t('bill.budget_alert_threshold_type_extra')}${this.form.fd.alert_threshold}%`
    },
  },
  watch: {
    recipientOpts (val) {
      const { user_ids } = this.$route.query
      if (val && !user_ids) {
        if (val.length === 1) {
          this.form.fc.setFieldsValue({
            [this.decorators.recipients[0]]: [val[0].id],
          })
          this.contactArrOptsChange([val[0].id])
        } else if (val.length > 1) {
          const currentUser = this.$store.getters.userInfo.id
          const hasCurrentUser = val.find(val => val.id === currentUser)
          if (hasCurrentUser) {
            this.form.fc.setFieldsValue({
              [this.decorators.recipients[0]]: [hasCurrentUser.id],
            })
            this.contactArrOptsChange([hasCurrentUser.id])
          }
        }
      }
    },
    contactArrOpts () {
      const ect = this.form.fc.getFieldValue('channel')
      if (ect) {
        const newContactTypes = this.contactArrOpts.filter((c) => { return ect.indexOf(c.value) >= 0 }).map((c) => c.value)
        this.form.fc.setFieldsValue({ channel: newContactTypes })
      }
    },
  },
  created () {
    this.fetchConfig()
    this.initRecipientParams(this.recipientParams)
  },
  methods: {
    contactArrOptsChange (rs) {
      const getLabel = (val) => {
        if (val === 'mobile') val = 'message' // mobile 应该翻译为 短信
        if (this.$t(`common.${val}`)) {
          return this.$t(`common.${val}`)
        }
        return val
      }
      if (rs) {
        const ect = this.recipientOpts.filter((opt) => {
          return rs.indexOf(opt.id) >= 0
        }).map((opt) => {
          return opt.enabled_contact_types
        })
        this.contactArrOpts = _.intersection(...ect).map((c) => {
          return {
            value: c,
            label: getLabel(c),
            disabled: c === 'webconsole',
          }
        })
      }
    },
    channelOptsChange (val) {
      const vs = val.filter((item) => {
        return isNaN(item.disabled)
      })
      this.hadRobot = vs.length > 0
    },
    validateAlertThreshold (rule, value, callback) {
      if (/\d/.test(+value) && +value > 0) {
        callback()
      } else {
        callback(new Error(this.$t('bill.budget_alert_threshold_error')))
      }
    },
    async fetchConfig () {
      try {
        const { data: { types } } = await new this.$Manager('receivers', 'v1').performClassAction({ action: 'get-types', params: { $t: uuid() } })
        this.contactArrOpts = types.filter((item) => {
          return !item.includes('robot')
        }).map((item) => {
          return {
            label: contactMap[item].label,
            value: item,
            disabled: item === 'webconsole',
          }
        })
      } catch (error) {
        throw error
      }
    },
    initRecipientParams (recipientParams) {
      recipientParams.scope = this.scope
      if (this.isDomainMode) {
        recipientParams.project_domain_filter = true
      }
    },
  },
}
</script>
